import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const baseInputVariants = cva(
  "flex w-full rounded-lg border bg-white transition-all duration-200 file:border-0 file:bg-transparent file:font-medium file:text-neutral-1300 placeholder:text-neutral-400 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-100 disabled:bg-Color-Background-neutral-disabled disabled:text-Color-Text-neutral-black disabled:border-Color-Stroke-neutral",
  {
    variants: {
      variant: {
        default:
          "border-Color-Stroke-neutral focus-visible:border-Color-Stroke-brand focus-visible:ring-2 focus-visible:ring-Color-Stroke-brand/20",
        error:
          "border-Color-Stroke-negative focus-visible:border-Color-Stroke-negative focus-visible:ring-2 focus-visible:ring-Color-Stroke-negative/20",
        success:
          "border-Color-Semantic-positive focus-visible:border-Color-Semantic-positive focus-visible:ring-2 focus-visible:ring-Color-Semantic-positive/20",
      },
      size: {
        sm: "h-9 px-3 py-2 text-sm",
        md: "h-10 px-3 py-2 text-sm",
        lg: "h-11 px-4 py-3 text-base",
        mobile: "h-12 px-4 py-3 text-base", // Better touch target
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

export interface BaseInputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof baseInputVariants> {
  /**
   * Label hiển thị phía trên input
   */
  label?: string;

  /**
   * Thông báo lỗi hiển thị phía dưới input
   */
  error?: string;

  /**
   * Thông báo hỗ trợ hiển thị phía dưới input
   */
  helperText?: string;

  /**
   * Icon hiển thị bên trái input
   */
  leftIcon?: React.ReactNode;

  /**
   * Icon hiển thị bên phải input
   */
  rightIcon?: React.ReactNode;

  /**
   * Addon hiển thị bên trái input (thường là text)
   */
  addonBefore?: React.ReactNode;

  /**
   * Addon hiển thị bên phải input (thường là text)
   */
  addonAfter?: React.ReactNode;

  /**
   * Container className
   */
  containerClassName?: string;

  /**
   * Label className
   */
  labelClassName?: string;
}

const BaseInput = React.forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      className,
      variant,
      size,
      type,
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      addonBefore,
      addonAfter,
      containerClassName,
      labelClassName,
      disabled,
      ...props
    },
    ref,
  ) => {
    // Generate unique ID using useId hook
    const generatedId = React.useId();
    const inputId = props.id || `input-${generatedId}`;

    // Determine variant based on error state
    const inputVariant = error ? "error" : variant;

    return (
      <div className={cn("space-y-1 w-full", containerClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "text-Color-Text-neutral-black text-sm font-normal font-['Be_Vietnam_Pro'] leading-5 block mb-1",
              disabled && "text-Color-Text-neutral-disabled",
              // error && "text-Color-Text-semantic-negative", // Label usually doesn't turn red in this design system, only input border and error text
              labelClassName,
            )}
          >
            {label}
            {props.required && (
              <span className="text-Color-Text-semantic-negative ml-1">*</span>
            )}
          </label>
        )}

        {/* Input Container */}
        <div className="relative flex">
          {/* Addon Before */}
          {addonBefore && (
            <div className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-Color-Stroke-neutral bg-neutral-50 text-neutral-600 text-sm">
              {addonBefore}
            </div>
          )}

          {/* Input Wrapper */}
          <div className="relative flex-1">
            {/* Left Icon */}
            {leftIcon && (
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-Color-Icon-default pointer-events-none">
                {leftIcon}
              </div>
            )}

            {/* Input */}
            <input
              id={inputId}
              type={type}
              className={cn(
                baseInputVariants({ variant: inputVariant, size }),
                leftIcon && "pl-10",
                rightIcon && "pr-10",
                addonBefore && "rounded-l-none",
                addonAfter && "rounded-r-none",
                "text-Color-Text-neutral-black font-['Be_Vietnam_Pro']",
                className,
              )}
              ref={ref}
              disabled={disabled}
              aria-invalid={error ? "true" : undefined}
              aria-describedby={(() => {
                if (error) return `${inputId}-error`;
                if (helperText) return `${inputId}-help`;
                return undefined;
              })()}
              {...props}
            />

            {/* Right Icon */}
            {rightIcon && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-Color-Icon-default">
                {rightIcon}
              </div>
            )}
          </div>

          {/* Addon After */}
          {addonAfter && (
            <div className="inline-flex items-center px-3 rounded-r-lg border border-l-0 border-Color-Stroke-neutral bg-neutral-50 text-neutral-600 text-sm">
              {addonAfter}
            </div>
          )}
        </div>

        {/* Helper Text / Error Message */}
        {(error || helperText) && (
          <div
            id={error ? `${inputId}-error` : `${inputId}-help`}
            className={cn(
              "text-xs mt-1",
              error
                ? "text-Color-Text-semantic-negative"
                : "text-Color-Text-neutral-supplement",
            )}
            role={error ? "alert" : undefined}
          >
            {error || helperText}
          </div>
        )}
      </div>
    );
  },
);

BaseInput.displayName = "BaseInput";

export { BaseInput };
